import styled from '@emotion/styled';
import { faMoon, faRocket, faSignIn, faSun } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Link } from 'react-router';
import { StatusWidget } from '../StatusWidget';
import { getGettingStartedLink, getSignInLink } from '../../../RoutesWidget';

export interface ToggleThemeButtonProps {
  dark: boolean;
  toggleTheme: () => void;
}

export const ToggleThemeButton: React.FC<ToggleThemeButtonProps> = (props) => {
  const { dark, toggleTheme } = props;
  return (
    <S.ThemeToggle
      type="button"
      role="switch"
      aria-label="Dark mode"
      aria-checked={dark}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={toggleTheme}
    >
      <S.ThemeMode $selected={!dark} aria-hidden="true">
        <FontAwesomeIcon icon={faSun} />
      </S.ThemeMode>
      <S.ThemeMode $selected={dark} aria-hidden="true">
        <FontAwesomeIcon icon={faMoon} />
      </S.ThemeMode>
    </S.ThemeToggle>
  );
};

export interface StatusPageLinkProps {}

export const StatusPageLink: React.FC<StatusPageLinkProps> = (props) => {
  return (
    <S.LinkSimple to="https://status.journeyapps.com">
      <StatusWidget />
    </S.LinkSimple>
  );
};

export interface StartBuildingLinkProps {
  className?: string;
}

export const StartBuildingLink: React.FC<StartBuildingLinkProps> = (props) => {
  const { className } = props;
  return (
    <S.StartBuilding className={className} to={getGettingStartedLink()}>
      Start building
      <S.LinkIcon icon={faRocket} />
    </S.StartBuilding>
  );
};

export const SignInLink: React.FC<StartBuildingLinkProps> = (props) => {
  const { className } = props;
  return (
    <S.LinkSimple className={className} to={getSignInLink()}>
      Sign in
      <S.LinkIcon icon={faSignIn} />
    </S.LinkSimple>
  );
};

namespace S {
  export const ThemeToggle = styled.button`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: center;
    width: 74px;
    min-height: 38px;
    padding: 3px;
    border: 1px solid ${(p) => p.theme.header.dropdownBorder};
    border-radius: 10px;
    background: ${(p) => p.theme.header.searchBackground};
    color: ${(p) => p.theme.header.textColor};
    cursor: pointer;

    &:hover {
      border-color: ${(p) => p.theme.header.textColor};
    }

    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.home.walkthroughButton.focus};
      outline-offset: 3px;
    }
  `;

  export const ThemeMode = styled.span<{ $selected: boolean }>`
    display: grid;
    place-items: center;
    height: 30px;
    border-radius: 7px;
    background: ${(p) =>
      p.$selected ? `color-mix(in srgb, ${p.theme.header.textColor} 14%, transparent)` : 'transparent'};
    color: ${(p) => p.theme.header.textColor};
    opacity: ${(p) => (p.$selected ? 1 : 0.55)};
  `;

  export const LinkIcon = styled(FontAwesomeIcon)``;

  export const LinkSimple = styled(Link)`
    text-decoration: none;
    column-gap: 5px;
    display: flex;
    align-items: center;
  `;

  export const StartBuilding = styled(LinkSimple)`
    display: inline-flex;
    justify-content: center;
    gap: 10px;
    min-height: 38px;
    box-sizing: border-box;
    padding: 0 16px;
    border: 1px solid ${(p) => p.theme.home.walkthroughButton.border};
    border-radius: 10px;
    background: ${(p) => p.theme.home.walkthroughButton.background};
    color: ${(p) => p.theme.text.heading};
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    transition: background-color 160ms ease;
    &:hover {
      background: ${(p) => p.theme.home.walkthroughButton.hoverBackground};
      text-decoration: none;
    }
    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.home.walkthroughButton.focus};
      outline-offset: 3px;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `;
}
